/**
 * UserMenuBrand — the rail's brand mark doubles as the user-menu trigger.
 *
 * The black 36×36 brand box shows the **initials of the active company**
 * (derived from `me.data.membership.Nazev`) rather than a static "A".
 * Clicking it opens a dropdown with user info, company switcher (when >1
 * company), theme controls, and Odhlásit se. Replaces the earlier
 * fixed-position top-right chip — the rail brand is the single entry point
 * for everything user-scoped.
 *
 * Owns the one-shot theme seed from `me.data.user.theme_preference` so the
 * server's stored preference wins over localStorage on first paint after
 * login.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Icon } from './Icon';
import { Menu } from './Menu';
import { Modal } from './Modal';
import { SegmentedControl } from './SegmentedControl';
import { Button } from './Button';
import { api } from '@/lib/api';
import { clearApiToken, readApiToken } from '@/lib/auth';
import { useTheme, type ThemeMode } from '@/lib/theme';
import {
  useMeQuery,
  useMyCompaniesQuery,
  useSelectCompanyMutation,
} from '@/modules/dashboard/api';
import { useUpdatePreferencesMutation, useResolveSlackMutation } from '@/modules/auth/api';
import { toast } from './Toaster';

interface UserMenuBrandProps {
  /** Fallback letter when membership data hasn't loaded yet or has no Nazev. */
  fallbackLetter?: string;
  /** Margin/positioning of the brand button. Defaults to the rail-head
   * spacing; AppTopBar passes '' to sit flush in the 56px phone bar. */
  className?: string;
}

/** Czech legal-form suffixes stripped before computing company initials. */
const COMPANY_LEGAL_SUFFIX_RE =
  /\s+(s\.r\.o\.?|a\.s\.?|spol\.?\s*s\s*r\.o\.?|k\.s\.?|v\.o\.s\.?|o\.p\.s\.?)\s*$/i;

export function companyInitials(name: string | null | undefined, fallback = 'A'): string {
  if (!name) return fallback;
  const cleaned = name.replace(COMPANY_LEGAL_SUFFIX_RE, '').trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function UserMenuBrand({ fallbackLetter = 'A', className = 'mb-4' }: UserMenuBrandProps) {
  const me = useMeQuery();
  const companies = useMyCompaniesQuery();
  const selectCompany = useSelectCompanyMutation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const theme = useTheme();
  const seededFromServer = useRef(false);

  // Seed the theme from the server's stored preference once per session.
  // localStorage stays as the optimistic / pre-paint source (inline script in
  // index.html applies it before React mounts), but server wins on first
  // /me arrival so the choice rides between devices.
  useEffect(() => {
    if (seededFromServer.current) return;
    const serverTheme = me.data?.user?.theme_preference;
    if (!serverTheme) return;
    if (serverTheme !== theme.mode) theme.setMode(serverTheme);
    seededFromServer.current = true;
  }, [me.data, theme]);

  // Defensive — some routes (e.g. /health) share the ['auth', 'me'] cache
  // key but mock a slimmer payload. Render a plain brand mark in that case.
  const user = me.data?.user;
  const isAuthed = !me.error && !!user && typeof user.nick === 'string';

  if (!isAuthed) {
    return <BrandMark letter={fallbackLetter} className={className} />;
  }

  const safeUser = user!;
  const membership = me.data?.membership;
  const firmaNazev = membership?.Nazev ?? '';
  const allCompanies = companies.data?.companies ?? [];
  const multipleCompanies = allCompanies.length > 1;
  const brandLetters = companyInitials(firmaNazev || null, fallbackLetter);

  function handlePickCompany(companyId: number) {
    // Bearer is required by `/v1/auth/select-company` (updates api_tokens).
    // The SPA mints its own bearer at login, so by the time this picker is
    // reachable a token is present. If it's somehow absent the user is
    // effectively unauthenticated — send them to login.
    if (!readApiToken()) {
      navigate({ to: '/login' });
      return;
    }
    selectCompany.mutate(companyId, {
      onSuccess: () => {
        setPickerOpen(false);
        toast.ok('Firma přepnuta');
      },
      onError: (e) => {
        toast.error(`Chyba: ${e instanceof Error ? e.message : 'přepnutí firmy se nezdařilo'}`);
      },
    });
  }

  async function handleLogout() {
    // Best-effort revoke server-side, but the local teardown is what really
    // matters. Any logout error (no Bearer → 400, expired token → 401, 5xx)
    // is moot once we've cleared the local token + cache and navigated to
    // the login screen, so we silently swallow it.
    try {
      await api.delete('/v1/auth/logout');
    } catch {
      // ignore
    }
    clearApiToken();
    qc.clear();
    navigate({ to: '/login' });
  }

  return (
    <>
      <Menu>
        <Menu.Trigger>
          <button
            type="button"
            data-testid="user-menu-trigger"
            aria-label={`${safeUser.nick}${firmaNazev ? ` · ${firmaNazev}` : ''}`}
            className={`${className} grid size-9 cursor-pointer place-items-center bg-ink font-mono text-[14px] font-bold tracking-[-0.05em] text-paper outline-none transition-opacity hover:opacity-90 focus-visible:opacity-90`}
          >
            {brandLetters}
          </button>
        </Menu.Trigger>
        <Menu.Content side="right" align="start" sideOffset={10} minWidth={280}>
          <Menu.Label>
            <span className="block text-ink">{safeUser.jmeno} {safeUser.prijmeni}</span>
            {firmaNazev && (
              <span className="mt-0.5 block normal-case tracking-normal text-ink-3 [text-transform:none]">
                {firmaNazev}
              </span>
            )}
          </Menu.Label>
          {multipleCompanies && (
            <Menu.Item
              icon={<Icon name="building" size={13} />}
              onSelect={() => {
                // No preventDefault — let Radix close the menu naturally so it
                // doesn't sit open behind the company-picker modal. The state
                // update batches with React's so the modal opens in the same
                // commit the menu closes.
                setPickerOpen(true);
              }}
            >
              Přepnout firmu
            </Menu.Item>
          )}
          <Menu.Separator />
          <div className="px-3 py-2">
            <div className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.06em] text-ink-3">
              Motiv
            </div>
            <ThemeSwitcherWithPersist />
          </div>
          {Boolean(membership?.slack_enabled) && (
            <>
              <Menu.Separator />
              <div className="px-3 py-2">
                <div className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.06em] text-ink-3">
                  Slack notifikace
                </div>
                <SlackPairing
                  paired={Boolean(safeUser.slack_user_id)}
                  enabled={safeUser.slack_enabled !== 0}
                />
              </div>
            </>
          )}
          <Menu.Separator />
          <Menu.Item
            icon={<Icon name="close" size={13} />}
            danger
            onSelect={() => {
              void handleLogout();
            }}
          >
            Odhlásit se
          </Menu.Item>
        </Menu.Content>
      </Menu>

      <Modal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        ariaLabel="Přepnout firmu"
        maxWidth={480}
      >
        <Modal.Body className="p-6">
          <h2 className="mb-4 font-display text-[28px] leading-none">
            Vyberte firmu.
          </h2>
          <ul className="divide-y divide-line border-y border-line">
            {allCompanies.map((co) => (
              <li key={co.id}>
                <button
                  type="button"
                  disabled={selectCompany.isPending}
                  onClick={() => handlePickCompany(co.id)}
                  className={
                    'flex w-full items-center justify-between px-3 py-3 text-left text-[13px] hover:bg-accent-soft disabled:opacity-50 ' +
                    (co.id === me.data?.company_id ? 'bg-accent-soft font-semibold' : '')
                  }
                >
                  <span>{co.name}</span>
                  {co.id === me.data?.company_id && (
                    <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-accent-text">
                      Aktivní
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-5 flex justify-end">
            <Button variant="secondary" onClick={() => setPickerOpen(false)}>
              Zavřít
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

function BrandMark({ letter, className = 'mb-4' }: { letter: string; className?: string }) {
  return (
    <div
      className={`${className} grid size-9 place-items-center bg-ink font-mono text-[14px] font-bold tracking-[-0.05em] text-paper`}
    >
      {letter}
    </div>
  );
}

/**
 * Per-user Slack pairing controls in the user menu. Shown only when the
 * company has Slack enabled. Pairing is normally automatic on first task
 * notification (resolved by e-mail), so this is mostly a status readout + a
 * manual "pair now" button and a per-user opt-out.
 */
function SlackPairing({ paired, enabled }: { paired: boolean; enabled: boolean }) {
  const resolve = useResolveSlackMutation();
  const persist = useUpdatePreferencesMutation();

  function handleResolve() {
    resolve.mutate(undefined, {
      onSuccess: () => toast.ok('Spárováno se Slackem'),
      onError: (e) =>
        toast.error(`Chyba: ${e instanceof Error ? e.message : 'spárování se nezdařilo'}`),
    });
  }

  function handleToggle(next: boolean) {
    persist.mutate(
      { slack_enabled: next ? 1 : 0 },
      {
        onError: (e) =>
          toast.error(`Chyba: ${e instanceof Error ? e.message : 'uložení se nezdařilo'}`),
      },
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <label className="flex items-center gap-2 text-[12.5px] text-ink-2">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => handleToggle(e.target.checked)}
          className="size-3.5 accent-[var(--accent)]"
        />
        Dostávat notifikace
      </label>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10.5px] text-ink-3">
          {paired ? 'spárováno ✓' : 'nespárováno'}
        </span>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          mono
          disabled={resolve.isPending}
          onClick={handleResolve}
        >
          {resolve.isPending ? 'Páruji…' : paired ? 'Přepárovat' : 'Spárovat'}
        </Button>
      </div>
    </div>
  );
}

/**
 * ThemeSwitcher variant used inside the user menu — optimistically updates
 * the ThemeProvider (so the UI flips immediately) and persists the choice via
 * `PATCH /v1/auth/me/preferences` so the preference rides between devices.
 * On error, reverts and toasts. The plain `<ThemeSwitcher />` (no mutation)
 * remains usable on public routes like /login.
 */
function ThemeSwitcherWithPersist() {
  const theme = useTheme();
  const persist = useUpdatePreferencesMutation();

  function handleChange(next: ThemeMode) {
    const previous = theme.mode;
    theme.setMode(next);
    persist.mutate(
      { theme_preference: next },
      {
        onError: (e) => {
          theme.setMode(previous);
          toast.error(
            `Chyba: ${e instanceof Error ? e.message : 'uložení motivu se nezdařilo'}`,
          );
        },
      },
    );
  }

  return (
    <SegmentedControl<ThemeMode>
      aria-label="Motiv"
      value={theme.mode}
      onChange={handleChange}
      options={[
        { value: 'system', label: 'AUTO' },
        {
          value: 'light',
          label: (
            <span className="inline-flex items-center gap-1.5">
              <Icon name="sun" size={11} />
              LIGHT
            </span>
          ),
        },
        {
          value: 'dark',
          label: (
            <span className="inline-flex items-center gap-1.5">
              <Icon name="moon" size={11} />
              DARK
            </span>
          ),
        },
      ]}
    />
  );
}
